import {
  Button,
  convertToKST,
  isKST,
  Modal,
  Styles,
  useGetCompanyInfo,
} from '@orcar/common-d2c';
import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dropdown } from '@/components/dropdown';
import { TODAY_RESERVATION_DEADLINE } from '@/constants/date';
import {
  getAvailablePickUpTimeOptions,
  getRentalOperatingHours,
  getTimeOptions,
} from '@/utils/date';
import * as S from './styles';
import { WebCalendar } from '../WebCalendar';

interface DateTimeOptionProps {
  pickUpDate: Dayjs;
  setPickUpDate: Dispatch<SetStateAction<Dayjs>>;
  dropOffDate: Dayjs;
  setDropOffDate: Dispatch<SetStateAction<Dayjs>>;
  isMobile?: boolean;
  pickUpTimeValue: number;
  dropOffTimeValue: number;
  setPickUpTimeValue: Dispatch<SetStateAction<number>>;
  setDropOffTimeValue: Dispatch<SetStateAction<number>>;
  onClick: () => void;
}

const DateTimeOption = ({
  pickUpDate,
  dropOffDate,
  pickUpTimeValue,
  dropOffTimeValue,
  setPickUpDate,
  setDropOffDate,
  setPickUpTimeValue,
  setDropOffTimeValue,
  onClick,
}: DateTimeOptionProps) => {
  const [currentDate, setCurrentDate] = useState(
    pickUpDate ? pickUpDate.set('date', 1) : dayjs().set('date', 1),
  );
  // const [showTwoMonthWarning, setShowTwoMonthWarning] = useState(false);

  const todayInKST = dayjs().tz('Asia/seoul');
  const pickUpAtInKST = dayjs(convertToKST(pickUpDate?.format())).tz(
    'Asia/seoul',
  );
  const isToday = dayjs(pickUpAtInKST).isSame(todayInKST, 'date');

  const handleChangeCalendar = (direction: string) => {
    if (direction === 'previous')
      return setCurrentDate(currentDate.subtract(1, 'month'));
    else return setCurrentDate(currentDate.add(1, 'month'));
  };

  const handleApplyClick = () => {
    // const twoMonthsLater = dayjs().add(2, 'month');
    // if (pickUpDate.isAfter(twoMonthsLater)) {
    //   setShowTwoMonthWarning(true);
    //   return;
    // }
    onClick();
  };

  const { data: companyData } = useGetCompanyInfo();
  const rentalOperatingHours = getRentalOperatingHours(companyData);

  const pickUpTimeOptions = getTimeOptions('pickUp', rentalOperatingHours);
  const dropOffTimeOptions = getTimeOptions('dropOff', rentalOperatingHours);
  const availablePickUpTimeOptions =
    getAvailablePickUpTimeOptions(rentalOperatingHours);

  const pickUpOptions = isToday
    ? availablePickUpTimeOptions
    : pickUpTimeOptions;

  const dropOffOptions = pickUpDate?.isSame(dropOffDate, 'date')
    ? dropOffTimeOptions.filter((option) => option.value > pickUpTimeValue)
    : dropOffTimeOptions;

  const isEarlyDropOff = dropOffTimeValue <= 730;

  return (
    <>
      <Wrapper>
        <InnerWrapper>
          <S.DateSection>
            <WebCalendar
              year={currentDate.year()}
              month={currentDate.month() + 1}
              pickUpDate={pickUpDate}
              dropOffDate={dropOffDate}
              onSelectPickup={setPickUpDate}
              onSelectDropOff={setDropOffDate}
              onClickMoveCalendar={handleChangeCalendar}
              todayReservationDeadline={TODAY_RESERVATION_DEADLINE}
            />
          </S.DateSection>
          <S.TimeSection>
            <S.DropdownSection>
              <Title>대여 시간</Title>
              <Dropdown
                options={pickUpOptions}
                selectedValue={pickUpTimeValue}
                onSelect={setPickUpTimeValue}
              />
            </S.DropdownSection>
            <S.DropdownSection>
              <Title>반납 시간</Title>
              <Dropdown
                options={dropOffOptions}
                selectedValue={dropOffTimeValue}
                onSelect={setDropOffTimeValue}
              />
            </S.DropdownSection>
            {isEarlyDropOff && (
              <S.Text color={Styles.COLOR.DANGER}>
                선택하신 반납 시간은 새벽 반납 비용 1만원이 추가됩니다.
              </S.Text>
            )}
            {!isKST && <S.Text>한국 시간 기준입니다.</S.Text>}
          </S.TimeSection>
        </InnerWrapper>
        <ButtonWrapper>
          <Button
            width='438px'
            height='45px'
            font={{
              'font-size': '16px',
              'font-weight': '600',
              'line-height': '27px',
            }}
            borderRadius='5px'
            onClick={handleApplyClick}
            disabled={!pickUpDate || !dropOffDate}
          >
            적용하기
          </Button>
        </ButtonWrapper>
      </Wrapper>
      {/* {showTwoMonthWarning && (
        <Modal
          onClose={() => setShowTwoMonthWarning(false)}
          paddingHorizontal={33}
          paddingVertical={40}
          yPosition='450px'
          hasBorder={false}
          topLevel
        >
          <div className='flex flex-col items-center justify-center p-6'>
            <p className='mb-6 text-center text-xl font-semibold leading-relaxed'>
              2개월 이후 예약을 원하시면
              <br />
              <span className='text-primary'>제주로 렌터카</span>로 문의
              부탁드립니다
            </p>
            <p className='mb-6 text-gray-500'>TEL: 064-744-1116</p>
            <Button
              width='200px'
              height='45px'
              borderRadius='5px'
              font={{
                'font-size': '16px',
                'font-weight': '600',
                'line-height': '27px',
              }}
              onClick={() => setShowTwoMonthWarning(false)}
            >
              확인
            </Button>
          </div>
        </Modal>
      )} */}
    </>
  );
};

export default DateTimeOption;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  width: 475px;
  height: 360px;
  border-radius: 8px;
`;

const InnerWrapper = styled.div`
  display: flex;
  padding: 20px;
`;

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 69px;
`;

const Title = styled.h3<{ isMobile?: boolean }>`
  margin: 0;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.COLOR.GREY_02};
  ${({ theme, isMobile }) => !isMobile && theme.FONT.B2};
  line-height: normal;
  font-weight: 400;
`;
